import React, { useCallback, useState } from 'react';

import { Permissions } from '@fragment/authorization-utils/permissions';
import { LinkIcon } from '@fragment/ui/src/components/Icon/LinkIcon/LinkIcon';
import { Menu } from '@fragment/ui/src/components/Menu/Menu';

import type { LinkType } from '../../link/types';
import { useGetLinksQuery } from '../../../hooks/customerApi';
import { useCustomerApiContext } from '../../../hooks/useCustomerApiContext';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { useHasWorkspacePermission } from '../../../hooks/useHasWorkspacePermission';
import { getResultOrError } from '../../../utils/errors';
import { LinksTable } from '../../link/LinksTable';
import { linkTypeToName } from '../../link/types';
import { TableLoadError } from '../../TableLoadError/TableLoadError';
import { SettingsModalLayout } from '../SchemasV2/WorkspaceSettings/SettingsModalLayout';
import { AddLinkView, useEnabledLinkTypes } from './AddLinkView';

export const IntegrationsMainView = () => {
  const { context } = useCustomerApiContext();
  const [{ data, error, fetching }, reExecuteQuery] = useGetLinksQuery({
    context,
    pause: !context.token,
  });
  const { isError, result: links } = getResultOrError(data?.links, error);
  const canAddLink = useHasWorkspacePermission(Permissions.link.create);
  const linkTypes = useEnabledLinkTypes();
  const refresh = useCallback(
    () => reExecuteQuery({ requestPolicy: 'network-only' }),
    [reExecuteQuery]
  );
  const [addingLinkType, setAddingLinkType] = useState<LinkType | undefined>(
    undefined
  );
  useDocumentTitle('Links');
  const closeAddLink = useCallback(() => setAddingLinkType(undefined), []);
  const onCreate = useCallback(() => {
    refresh();
    closeAddLink();
  }, [refresh, closeAddLink]);

  if (addingLinkType) {
    return (
      <SettingsModalLayout
        title={`Add ${linkTypeToName[addingLinkType]} Link`}
        onBackButtonClick={closeAddLink}
      >
        <AddLinkView linkType={addingLinkType} onCreate={onCreate} />
      </SettingsModalLayout>
    );
  }

  return (
    <SettingsModalLayout
      title="Integrations"
      topButtonSection={
        canAddLink && !isError ? (
          <Menu
            items={linkTypes.map((type) => ({
              key: type,
              display: (
                <span className="flex items-center gap-x-[1ch]">
                  <LinkIcon type={type} />
                  <span>{linkTypeToName[type]}</span>
                </span>
              ),
              onClick: () => setAddingLinkType(type),
            }))}
            justify="justify-end"
            dataTestId="add-link-button"
          >
            Add+
          </Menu>
        ) : null
      }
    >
      {isError ? (
        <TableLoadError
          entityColumnName="Link"
          rightColumnName="ID"
          {...{ error, retry: refresh, fetching }}
        />
      ) : (
        <LinksTable data={links.nodes} />
      )}
    </SettingsModalLayout>
  );
};
